import type { HostingStatus, Partner } from "@lumora/bridge";
import { useViewModel } from "@lumora/ui";
import { useEffect, useState } from "react";
import { usePartnerVm } from "./platformViewModels.js";
import { ConfirmGate } from "./ConfirmGate.js";
import {
  bpsToPercent,
  buildPutInput,
  checkSplit,
  hostingStatusLines,
  partnersNotReady,
  rowsFromSitePartners,
  type SplitRow,
} from "./partnerView.js";
import { StatusMessage } from "./SiteAccountsList.js";

export const AUTO_SPLIT_LABEL =
  "Auto-send each paid hosting payment to partners' Stripe balances";

export type SplitEditorProps = {
  rows: SplitRow[];
  partners: Partner[];
  autoSplit: boolean;
  saving?: boolean;
  onChange: (rows: SplitRow[]) => void;
  onAutoSplit: (v: boolean) => void;
  onSave: () => void;
};

/** Presentational split editor: Platform row fixed, partner rows add/remove, live 100% check. */
export function SplitEditor({
  rows,
  partners,
  autoSplit,
  saving,
  onChange,
  onAutoSplit,
  onSave,
}: SplitEditorProps) {
  const check = checkSplit(rows);
  const ready: Record<string, boolean> = {};
  for (const p of partners) ready[p.slug] = p.connectReady;
  const notReady = partnersNotReady(rows, ready);
  const used = new Set(rows.map((r) => r.partnerSlug));
  const canAdd = partners.some((p) => !used.has(p.slug));

  const setRow = (key: string, patch: Partial<SplitRow>) =>
    onChange(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  return (
    <div className="platform-partner-split">
      <ul className="platform-account-list">
        {rows.map((r) => (
          <li key={r.key} className="platform-account-row">
            {r.partnerSlug === null ? (
              <strong>Platform (fixed row)</strong>
            ) : (
              <label>
                Partner
                <select
                  value={r.partnerSlug}
                  onChange={(e) =>
                    setRow(r.key, { partnerSlug: e.target.value })
                  }
                >
                  <option value="">Choose partner…</option>
                  {partners.map((p) => (
                    <option
                      key={p.slug}
                      value={p.slug}
                      disabled={used.has(p.slug) && p.slug !== r.partnerSlug}
                    >
                      {p.name} ({p.slug})
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label>
              Share (%)
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                inputMode="decimal"
                value={r.percent}
                onChange={(e) => setRow(r.key, { percent: e.target.value })}
              />
            </label>
            {r.partnerSlug !== null ? (
              <button
                type="button"
                className="ops-btn ops-btn-ghost"
                onClick={() => onChange(rows.filter((x) => x.key !== r.key))}
              >
                Remove
              </button>
            ) : null}
          </li>
        ))}
      </ul>

      <button
        type="button"
        className="ops-btn ops-btn-ghost"
        disabled={!canAdd}
        onClick={() =>
          onChange([
            ...rows,
            {
              key: `new-${rows.length}-${Date.now()}`,
              partnerSlug: "",
              percent: "0",
            },
          ])
        }
      >
        Add partner
      </button>
      {partners.length === 0 ? (
        <p className="muted platform-provision-status">
          No partners yet. Create one on the Sites page first.
        </p>
      ) : null}

      <p
        className={
          check.valid
            ? "muted platform-provision-status"
            : "ops-error platform-provision-status"
        }
        role="status"
        aria-live="polite"
        data-split-total
      >
        Total: {bpsToPercent(check.totalBps)}%
        {check.valid
          ? " — ready to save."
          : ` — ${check.problem ?? "Total must equal 100%."}`}
      </p>

      <label className="platform-provision-status">
        <input
          type="checkbox"
          checked={autoSplit}
          onChange={(e) => onAutoSplit(e.target.checked)}
        />
        {AUTO_SPLIT_LABEL}
      </label>
      <p className="muted platform-provision-status">
        Auto-split only works when every partner has finished Stripe onboarding.
        Until then, pay partners manually and log it in their ledger.
      </p>
      <ul className="platform-account-list">
        {rows
          .filter((r) => r.partnerSlug)
          .map((r) => (
            <li
              key={`ready-${r.key}`}
              className="muted platform-provision-status"
            >
              {r.partnerSlug}: Stripe onboarding{" "}
              {ready[r.partnerSlug as string] ? "complete" : "not complete"}
            </li>
          ))}
      </ul>
      {autoSplit && notReady.length > 0 ? (
        <p className="ops-error platform-provision-status" role="status">
          Auto-split is on but Stripe onboarding is incomplete for:{" "}
          {notReady.join(", ")}.
        </p>
      ) : null}

      {check.valid && !saving ? (
        <ConfirmGate
          idleLabel="Save split"
          confirmLabel="Yes, save split"
          onConfirm={onSave}
        />
      ) : (
        <button type="button" className="ops-btn" disabled>
          Save split
        </button>
      )}
    </div>
  );
}

/** Presentational hosting block. `status === null` while loading. */
export function HostingStatusBlock({
  status,
  error,
}: {
  status: HostingStatus | null;
  error?: string | null;
}) {
  if (error) {
    return (
      <p className="ops-error platform-provision-status" role="status">
        {error}
      </p>
    );
  }
  if (!status) {
    return (
      <p className="muted platform-provision-status" role="status">
        Loading hosting status…
      </p>
    );
  }
  const l = hostingStatusLines(status);
  return (
    <div data-hosting-status>
      <h4>Hosting status</h4>
      <dl className="platform-site-meta">
        <div>
          <dt>Subscription</dt>
          <dd>{l.subscription}</dd>
        </div>
        <div>
          <dt>Card on file</dt>
          <dd>{l.card}</dd>
        </div>
        <div>
          <dt>Last invoice</dt>
          <dd className="platform-provision-status">{l.invoice}</dd>
        </div>
        <div>
          <dt>Payment timing</dt>
          <dd>
            <span className={status.pastDue ? "ops-error" : "ops-live"}>
              {l.timing}
            </span>
          </dd>
        </div>
      </dl>
    </div>
  );
}

/** Admin: partner revenue split + hosting status for one site. */
export function PartnerSplitPanel({ slug }: { slug: string }) {
  const vm = usePartnerVm();
  const {
    partners,
    sitePartners,
    hosting,
    hostingError: hostingErr,
    message: msg,
    error: err,
    saving,
  } = useViewModel(vm);
  // Editable draft of the split; reseeded whenever the VM loads or saves a split.
  const [rows, setRows] = useState<SplitRow[] | null>(null);
  const [autoSplit, setAutoSplit] = useState(false);

  useEffect(() => {
    void vm.loadSplit(slug);
  }, [vm, slug]);

  useEffect(() => {
    if (!sitePartners) return;
    setRows(rowsFromSitePartners(sitePartners));
    setAutoSplit(sitePartners.autoSplit);
  }, [sitePartners]);

  return (
    <section className="ops-panel platform-provision-card" data-partner-split>
      <h3>Partner</h3>
      <p className="muted">
        Split each paid hosting payment between the platform and delivery
        partners. Shares must add up to exactly 100%.
      </p>
      {rows === null && !err ? (
        <p className="muted platform-provision-status" role="status">
          Loading partner split…
        </p>
      ) : null}
      {rows ? (
        <SplitEditor
          rows={rows}
          partners={partners}
          autoSplit={autoSplit}
          saving={saving}
          onChange={setRows}
          onAutoSplit={setAutoSplit}
          onSave={() => void vm.saveSplit(slug, buildPutInput(rows, autoSplit))}
        />
      ) : null}
      <StatusMessage msg={msg} err={err} />
      <HostingStatusBlock status={hosting} error={hostingErr} />
    </section>
  );
}
